import { Injectable, InjectionToken, Provider } from '@angular/core';
import { mockAssignments, mockEndOfCares } from '../../mock-data/assignment.mock';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { ApiResponse, Metadata } from '../api-types/api-response.api';
import {
	ApiAssignment,
	ApiAssignmentContactsBatchItem,
	ApiAssignmentContactsBatchRequest,
	ApiAssignmentEndOfCare,
	ApiAssignmentWithRelations,
	ApiAssingmentImpedimentReportMinMaxDueDate,
	CreateApiAssignmentBody,
	UpdateApiAssignmentBody,
} from '../api-types/assignment.api';
import { isResetToPhaseOneDecision, ResetToPhaseOneDecision } from '../api-types/reset-to-phase-one.api';
import {
	Assignment,
	AssignmentEndOfCare,
	AssignmentWithRelations,
	AssingmentImpedimentReportMinMaxDueDate,
	CreateAssignmentBody,
	UpdateAssignmentBody,
} from '../app-types/assignment';
import {
	transformApiAssignmentsToAppAssignments,
	transformApiAssignmentWithRelationsToAppAssignmentWithRelations,
	transformApiAssingmentImpedimentReportMinMaxDueDateToAssingmentImpedimentReportMinMaxDueDate,
	transformApiEndOfCaresToAppEndOfCares,
	transformAppCreateAssignmentToApiCreateAssignment,
	transformUpdateAssignmentBodyToApiUpdateAssignmentBody,
} from '../transformers/assignment.transform';
import { transformApiBatchContactsToAppContacts } from '../transformers/contact.transform';
import { BaseRequestService } from './base-request.service';

export const ASSIGNMENT_API = new InjectionToken<AssignmentProvider>('ASSIGNMENT_API');

@Injectable()
export class AssignmentApi extends BaseRequestService implements AssignmentProvider {
	private resetToPhaseOneUrl = (clientId: number, assignmentId: number) =>
		`phaseTransitions/clients/${clientId}/assignments/${assignmentId}/resetToPhaseOne`;

	private assertPositiveIds = (clientId: number, assignmentId: number): void => {
		if (!Number.isInteger(clientId) || clientId <= 0 || !Number.isInteger(assignmentId) || assignmentId <= 0) {
			throw new Error('clientId and assignmentId must be positive integers');
		}
	};

	public getAll = async (clientId: number): Promise<Assignment[]> => {
		const response = await this.get<ApiAssignment[]>(`clients/${clientId}/assignments`);
		return transformApiAssignmentsToAppAssignments(response);
	};

	public getContactsBatch = async (
		clientId: number | string,
		assignmentIds: number[],
	): Promise<Record<number, Assignment['contacts']>> => {
		if (assignmentIds.length === 0) return {};

		const response = await this.post<ApiAssignmentContactsBatchItem[], ApiAssignmentContactsBatchRequest>(
			`clients/${clientId}/assignments/contacts/batch`,
			{ assignmentIds },
		);

		return response.reduce<Record<number, Assignment['contacts']>>((acc, item) => {
			acc[item.assignmentId] = transformApiBatchContactsToAppContacts(item.contacts ?? []);
			return acc;
		}, {});
	};

	// Used in create assignment, since we update a pre-existing assignment rather than create one
	public create = async (clientId: number | string, assignmentId: number | string, data: CreateAssignmentBody) => {
		const body = transformAppCreateAssignmentToApiCreateAssignment(data);
		const response = await this.put<{ id: number }, CreateApiAssignmentBody>(
			`clients/${clientId}/assignments/${assignmentId}`,
			body,
		);
		return response.id;
	};

	/**
	 * Gets the valid date ranges for ImpedimentReport Date
	 */
	public async getValidDateRanges(assignmentId: string | number) {
		const response = await this.get<ApiAssingmentImpedimentReportMinMaxDueDate>(
			`validation/assignments/${assignmentId}/reports/duedate`,
		);
		return transformApiAssingmentImpedimentReportMinMaxDueDateToAssingmentImpedimentReportMinMaxDueDate(response);
	}

	/**
	 * Basically the same endpoint as for create, but we keep it separate for clarity.
	 */
	public update = async (
		clientId: number | string,
		assignmentId: number | string,
		data: UpdateAssignmentBody,
		initialStatement?: boolean,
	) => {
		const body = transformUpdateAssignmentBodyToApiUpdateAssignmentBody(data);
		const response = await this.put<ApiAssignment, UpdateApiAssignmentBody>(
			`clients/${clientId}/assignments/${assignmentId}`,
			body,
			{ initialStatement },
		);
		return response.id ?? Number(assignmentId);
	};

	public single = async (
		clientId: number | string,
		assignmentId: number | string,
	): Promise<ApiResponse<AssignmentWithRelations | undefined, Metadata>> => {
		const apiAssignment = await this.get<ApiResponse<ApiAssignmentWithRelations, Metadata>>(
			`clients/${clientId}/assignments/${assignmentId}`,
		);
		if (!apiAssignment?.data) return { data: undefined, metadata: { editOnlyFields: [] } };
		const assignment = transformApiAssignmentWithRelationsToAppAssignmentWithRelations(apiAssignment.data);
		return { data: assignment, metadata: apiAssignment.metadata };
	};

	public setDraftPhase = async (
		clientId: string | number,
		assignmentId: string | number,
		toPhaseId: string | number,
	): Promise<void> => {
		await this.put<void, unknown>(
			`clients/${clientId}/assignments/${assignmentId}/transferToDraft/${toPhaseId}?transitionDraft=true`,
			undefined,
		);
	};

	public resetAssignment = async (
		clientId: string | number,
		assignmentId: string | number,
		phaseId: string | number,
	): Promise<void> => {
		await this.post<void, unknown>(`clients/${clientId}/assignments/${assignmentId}/reset/${phaseId}`, undefined);
	};

	public movePhase = async (
		clientId: string | number,
		assignmentId: string | number,
		toPhaseId: string | number,
		disabilityKindId?: number | string,
	): Promise<void> => {
		await this.post<
			void,
			{
				disabilityKindId?: number | string;
			}
		>(`phaseTransitions/clients/${clientId}/assignments/${assignmentId}/toPhase/${toPhaseId}`, {
			disabilityKindId: disabilityKindId,
		});
	};

	public moveServiceOfficeStatementPhase = async (
		clientId: string | number,
		assignmentId: string | number,
		toPhaseId: string | number,
	): Promise<void> => {
		await this.post<void, void>(
			`phaseTransitions/serviceOfficeStatements/clients/${clientId}/assignments/${assignmentId}/toPhase/${toPhaseId}`,
			undefined,
		);
	};

	public moveAdditionalAssignment = async (
		clientId: string | number,
		assignmentId: string | number,
		toPhaseId: string | number,
	): Promise<void> => {
		await this.post<void, void>(
			`phaseTransitions/additionalAssignments/clients/${clientId}/assignments/${assignmentId}/toPhase/${toPhaseId}`,
			undefined,
		);
	};

	public getResetToPhaseOneDecision = async (
		clientId: number,
		assignmentId: number,
	): Promise<ResetToPhaseOneDecision> => {
		this.assertPositiveIds(clientId, assignmentId);
		const response = await this.get<unknown>(this.resetToPhaseOneUrl(clientId, assignmentId));
		if (!isResetToPhaseOneDecision(response)) {
			throw new Error('Invalid reset to phase one decision response');
		}
		return response;
	};

	public resetToPhaseOne = async (clientId: number, assignmentId: number): Promise<void> => {
		this.assertPositiveIds(clientId, assignmentId);
		await this.post<void, undefined>(this.resetToPhaseOneUrl(clientId, assignmentId), undefined);
	};

	public startPhaseTransition = async (
		clientId: number | string,
		assignmentId: number | string,
		toPhaseId: number | string,
	): Promise<void> => {
		await this.put<void, void>(
			`clients/${clientId}/assignments/${assignmentId}/transferToDraft/${toPhaseId}`,
			undefined,
		);
	};

	public async assignmentEndOfCares(assignmentId: number | string, offset?: number, range?: number) {
		const response = await this.get<PaginatedResponse<ApiAssignmentEndOfCare>>(
			`assignments/${assignmentId}/endOfCares`,
			{ offset, range },
		);
		const entities = transformApiEndOfCaresToAppEndOfCares(response.entities);
		return {
			...response,
			entities,
		};
	}
}

@Injectable()
export class AssignmentMockApi extends BaseRequestService implements AssignmentProvider {
	public constructor() {
		super();
		console.warn('⛔️⛔️⛔️ Assignment Mock Service: You are currently using a mock service!');
	}
	public getAll(_clientId: number): Promise<Assignment[]> {
		console.warn('⛔️⛔️⛔️ Assignment Mock Service->getAll: Mock Data:', mockAssignments);
		return Promise.resolve(mockAssignments);
	}

	public getContactsBatch = async (
		_clientId: number | string,
		assignmentIds: number[],
	): Promise<Record<number, Assignment['contacts']>> => {
		const byId = mockAssignments.reduce<Record<number, Assignment['contacts']>>((acc, assignment) => {
			acc[assignment.id] = assignment.contacts;
			for (const additional of assignment.additionalAssignments ?? []) {
				acc[additional.id] = additional.contacts;
			}
			return acc;
		}, {});

		return assignmentIds.reduce<Record<number, Assignment['contacts']>>((acc, assignmentId) => {
			acc[assignmentId] = byId[assignmentId] ?? [];
			return acc;
		}, {});
	};

	public create = async (_clientId: number | string, _assignmentId: number | string, data: CreateAssignmentBody) => {
		const body = transformAppCreateAssignmentToApiCreateAssignment(data);
		console.warn('[Mock] Creating assignment with data:', body);
		const mockResponse = { id: 1 };
		return Promise.resolve(mockResponse.id);
	};

	public update = async (
		_clientId: number | string,
		_assignmentId: number | string,
		data: UpdateAssignmentBody,
		_initialStatement?: boolean,
	) => {
		const body = transformUpdateAssignmentBodyToApiUpdateAssignmentBody(data);
		console.warn('[Mock] Updating assignment with data:', body);
		const mockResponse = { id: 1 };
		return Promise.resolve(mockResponse.id);
	};

	public async getValidDateRanges(_assignmentId: string | number): Promise<AssingmentImpedimentReportMinMaxDueDate> {
		console.warn('⛔️⛔️⛔️ LEK Mock Service->getValidDateRanges: Mock Data:', _assignmentId);
		return {
			minStartDate: new Date('2026-03-03'),
			maxEndDate: new Date('2026-04-23'),
		};
	}

	public single = async (
		clientId: number | string,
		assignmentId: number | string,
	): Promise<ApiResponse<AssignmentWithRelations | undefined, Metadata>> => {
		console.warn(`[Mock] Fetching assignment ${assignmentId} for client ${clientId}`);
		const assignment = mockAssignments.find((a) => a.id === assignmentId);
		return Promise.resolve({ data: assignment, metadata: { editOnlyFields: [] } });
	};

	public movePhase = async (
		_clientId: string | number,
		assignmentId: string | number,
		toPhaseId: string | number,
		_disabilityKindId?: number | string,
	): Promise<void> => {
		console.warn(`[Mock] Moving assignment ${assignmentId} to phase ${toPhaseId}`);
		return Promise.resolve();
	};

	public moveServiceOfficeStatementPhase = async (
		_clientId: string | number,
		assignmentId: string | number,
		toPhaseId: string | number,
	): Promise<void> => {
		console.warn(`[Mock] Moving service office statement ${assignmentId} to phase ${toPhaseId}`);
		return Promise.resolve();
	};

	public moveAdditionalAssignment = async (
		_clientId: string | number,
		assignmentId: string | number,
		toPhaseId: string | number,
	): Promise<void> => {
		console.warn(`[Mock] Moving additional assignment ${assignmentId} to phase ${toPhaseId}`);
		return Promise.resolve();
	};

	public getResetToPhaseOneDecision = async (
		_clientId: number,
		_assignmentId: number,
	): Promise<ResetToPhaseOneDecision> => {
		return Promise.resolve({ canReset: true, reasons: [] });
	};

	public resetToPhaseOne = async (_clientId: number, assignmentId: number): Promise<void> => {
		console.warn(`[Mock] Assignment ${assignmentId} reset to phase one`);
		return Promise.resolve();
	};

	public startPhaseTransition = async (
		_clientId: number | string,
		assignmentId: number | string,
		toPhaseId: number | string,
	): Promise<void> => {
		console.warn(`[Mock] Starting phase transition for assignment ${assignmentId} to phase ${toPhaseId}`);
		return Promise.resolve();
	};

	public assignmentEndOfCares(assignmentId: number | string): Promise<PaginatedResponse<AssignmentEndOfCare>> {
		console.warn(`[Mock] Fetching end of cares for assignment ${assignmentId}`);
		return Promise.resolve({
			entities: mockEndOfCares,
			totalEntities: mockEndOfCares.length,
			offset: 0,
			limit: 10,
			range: 10,
		});
	}

	public setDraftPhase = async (
		_clientId: string | number,
		assignmentId: string | number,
		toPhaseId: string | number,
		_disabilityKindId?: number | string,
	): Promise<void> => {
		console.warn(`[Mock] Creating assignment draft ${assignmentId} to phase ${toPhaseId}`);
		return Promise.resolve();
	};

	public resetAssignment = async (_clientId: string | number, assignmentId: string | number): Promise<void> => {
		console.warn(`[Mock] Assignment ${assignmentId} reset`);
		return Promise.resolve();
	};
}

export const provideAssignmentApi: Provider = {
	provide: ASSIGNMENT_API,
	useClass: AssignmentApi,
};

export interface AssignmentProvider {
	update(
		clientId: number | string,
		assignmentId: number | string,
		data: UpdateAssignmentBody,
		initialStatement?: boolean,
	): Promise<number>;
	create(clientId: number | string, assignmentId: number | string, data: CreateAssignmentBody): Promise<number>;
	getAll(clientId: number): Promise<Assignment[]>;
	getContactsBatch(clientId: number | string, assignmentIds: number[]): Promise<Record<number, Assignment['contacts']>>;
	getValidDateRanges(assignmentId: string | number): Promise<AssingmentImpedimentReportMinMaxDueDate>;
	single(
		clientId: number | string,
		assignmentId: number | string,
	): Promise<ApiResponse<AssignmentWithRelations | undefined, Metadata>>;
	movePhase(
		clientId: string | number,
		assignmentId: string | number,
		toPhaseId: string | number,
		disabilityKindId?: number | string,
	): Promise<void>;
	moveServiceOfficeStatementPhase(
		clientId: string | number,
		assignmentId: string | number,
		toPhaseId: string | number,
	): Promise<void>;
	moveAdditionalAssignment(
		clientId: string | number,
		assignmentId: string | number,
		toPhaseId: string | number,
	): Promise<void>;
	/**
	 * Starts the phase transition for an assignment.
	 * This is not the actual move, but the initialization of the transition process.
	 * @param clientId ID of the client where the assignment belongs to
	 * @param assignmentId ID of the assignment which should start the transition
	 * @param toPhaseId ID of the phase to which we want to start the transition
	 */
	startPhaseTransition(
		clientId: number | string,
		assignmentId: number | string,
		toPhaseId: number | string,
	): Promise<void>;

	assignmentEndOfCares(
		assignmentId: number | string,
		offset?: number,
		range?: number,
	): Promise<PaginatedResponse<AssignmentEndOfCare>>;

	setDraftPhase(clientId: string | number, assignmentId: string | number, draftPhaseId: string | number): Promise<void>;

	resetAssignment(clientId: string | number, assignmentId: string | number, phaseId: string | number): Promise<void>;
	getResetToPhaseOneDecision(clientId: number, assignmentId: number): Promise<ResetToPhaseOneDecision>;
	resetToPhaseOne(clientId: number, assignmentId: number): Promise<void>;
}
